import { ExternalEmailContact } from "@accurx/api/clinician-messaging";
import { Ds } from "@accurx/design";
import {
    ContactFormFieldErrors,
    ContactFormSchema,
    ContactFormSubmission,
} from "domains/directory/schemas/contact-form/ContactFormSchema";

import { ContactInstitutionSelector } from "./ContactInstitutionSelector";
import { ContactServiceSelector } from "./ContactServiceSelector";

type DirectoryContactFormProps = {
    id?: string;
    errors?: ContactFormFieldErrors;
    onError: (errors: ContactFormFieldErrors) => void;
    onSubmit: (data: ContactFormSubmission) => void;
} & (
    | { emailAddress: string; contact?: never }
    | { emailAddress?: never; contact: ExternalEmailContact }
);

export const DirectoryContactForm = ({
    id,
    errors,
    onError,
    onSubmit,
    emailAddress,
    contact,
}: DirectoryContactFormProps) => {
    const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);

        const { error, data } = ContactFormSchema.safeParse({
            emailAddress: emailAddress ?? contact.email,
            displayName: formData.get("displayName"),
            serviceId: formData.get("serviceId") ?? contact?.service?.id,
            institutionId:
                formData.get("institutionId") ?? contact?.institution?.id,
            notes: formData.get("notes"),
            phoneNumber: formData.get("phoneNumber"),
            websiteUrl: formData.get("websiteUrl"),
        });
        if (error) {
            const errors = error.flatten();
            onError(errors.fieldErrors);
            return;
        }

        onSubmit(data);
    };

    return (
        <form id={id} noValidate onSubmit={handleSubmit}>
            <Ds.Flex flexDirection="column" gap="2">
                {emailAddress && (
                    <Ds.Form.Field name="emailAddress">
                        <Ds.Form.Label>Email</Ds.Form.Label>
                        <Ds.Form.Control>
                            <Ds.TextInput
                                type="text"
                                value={emailAddress}
                                isInvalid={!!errors?.emailAddress?.[0]}
                                disabled
                            >
                                <Ds.TextInput.Slot position="end">
                                    <Ds.Icon
                                        color="green"
                                        name="Success"
                                        appearance="solid"
                                    />
                                </Ds.TextInput.Slot>
                            </Ds.TextInput>
                        </Ds.Form.Control>
                        {errors?.emailAddress?.[0] && (
                            <Ds.Form.Error>
                                {errors.emailAddress[0]}
                            </Ds.Form.Error>
                        )}
                    </Ds.Form.Field>
                )}

                <Ds.Form.Field name="displayName">
                    <Ds.Form.Label>Display name</Ds.Form.Label>
                    <Ds.Form.Control>
                        <Ds.TextInput
                            autoFocus
                            type="text"
                            isInvalid={!!errors?.displayName?.[0]}
                            defaultValue={contact?.displayName ?? undefined}
                            required
                        />
                    </Ds.Form.Control>
                    {errors?.displayName?.[0] && (
                        <Ds.Form.Error>{errors.displayName[0]}</Ds.Form.Error>
                    )}
                </Ds.Form.Field>

                <Ds.Form.Field name="serviceId">
                    <Ds.Form.Label htmlFor="serviceId">Service</Ds.Form.Label>
                    <Ds.Form.Control>
                        <ContactServiceSelector
                            name="serviceId"
                            isInvalid={!!errors?.serviceId?.[0]}
                            selectedServiceId={
                                contact?.service?.id ?? undefined
                            }
                        />
                    </Ds.Form.Control>
                    {errors?.serviceId?.[0] && (
                        <Ds.Form.Error>{errors.serviceId[0]}</Ds.Form.Error>
                    )}
                </Ds.Form.Field>

                <Ds.Form.Field name="institutionId">
                    <Ds.Form.Label htmlFor="institutionId">
                        Organisation
                    </Ds.Form.Label>
                    <Ds.Form.Control>
                        <ContactInstitutionSelector
                            name="institutionId"
                            isInvalid={!!errors?.institutionId?.[0]}
                            selectedInstitutionId={
                                contact?.institution?.id ?? undefined
                            }
                        />
                    </Ds.Form.Control>
                    {errors?.institutionId?.[0] && (
                        <Ds.Form.Error>{errors.institutionId[0]}</Ds.Form.Error>
                    )}
                </Ds.Form.Field>

                <Ds.Form.Field name="notes">
                    <Ds.Form.Label>Note</Ds.Form.Label>
                    <Ds.Form.Control>
                        <Ds.Textarea
                            rows={3}
                            isInvalid={!!errors?.notes?.[0]}
                            defaultValue={contact?.notes ?? undefined}
                        />
                    </Ds.Form.Control>
                    {errors?.notes?.[0] && (
                        <Ds.Form.Error>{errors.notes[0]}</Ds.Form.Error>
                    )}
                </Ds.Form.Field>

                <Ds.Form.Field name="phoneNumber">
                    <Ds.Form.Label>Phone number</Ds.Form.Label>
                    <Ds.Form.Control>
                        <Ds.TextInput
                            type="text"
                            isInvalid={!!errors?.phoneNumber?.[0]}
                            defaultValue={contact?.phoneNumber ?? undefined}
                        />
                    </Ds.Form.Control>
                    {errors?.phoneNumber?.[0] && (
                        <Ds.Form.Error>{errors.phoneNumber[0]}</Ds.Form.Error>
                    )}
                </Ds.Form.Field>

                <Ds.Form.Field name="websiteUrl">
                    <Ds.Form.Label>Website</Ds.Form.Label>
                    <Ds.Form.Control>
                        <Ds.TextInput
                            type="text"
                            isInvalid={!!errors?.websiteUrl?.[0]}
                            defaultValue={contact?.websiteUrl ?? undefined}
                        />
                    </Ds.Form.Control>
                    {errors?.websiteUrl?.[0] && (
                        <Ds.Form.Error>{errors.websiteUrl[0]}</Ds.Form.Error>
                    )}
                </Ds.Form.Field>
            </Ds.Flex>
        </form>
    );
};
